import { useState, useEffect } from 'react';
import type { BuyerAddress, CreateBuyerAddressRequest, UpdateBuyerAddressRequest } from '../types';
import { createAddress, updateAddress, verifyAddress } from '../api/client';
import type { AddressVerificationResponse } from '../api/client';
import { colors } from '../constants/colors';
import { X } from 'lucide-react';

interface AddressFormModalProps {
  address?: BuyerAddress | null;
  onClose: () => void;
  onSaved: () => void;
}

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
];

export function AddressFormModal({ address, onClose, onSaved }: AddressFormModalProps) {
  const [name, setName] = useState('');
  const [streetLine1, setStreetLine1] = useState('');
  const [streetLine2, setStreetLine2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [isDefault, setIsDefault] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggestion, setSuggestion] = useState<AddressVerificationResponse | null>(null);

  const isEditMode = !!address;

  useEffect(() => {
    if (address) {
      setName(address.name);
      setStreetLine1(address.streetLine1);
      setStreetLine2(address.streetLine2 || '');
      setCity(address.city);
      setState(address.state);
      setZipCode(address.zipCode);
      setIsDefault(address.isDefault);
    } else {
      setName('');
      setStreetLine1('');
      setStreetLine2('');
      setCity('');
      setState('');
      setZipCode('');
      setIsDefault(true);
    }
    setError(null);
    setSuggestion(null);
  }, [address]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    if (!streetLine1.trim()) {
      setError('Street address is required');
      return;
    }
    if (!city.trim()) {
      setError('City is required');
      return;
    }
    if (!state) {
      setError('State is required');
      return;
    }
    if (!zipCode.trim() || !/^\d{5}(-\d{4})?$/.test(zipCode.trim())) {
      setError('Valid ZIP code is required (e.g., 12345 or 12345-6789)');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const verifyRequest: CreateBuyerAddressRequest = {
        name: name.trim(),
        streetLine1: streetLine1.trim(),
        streetLine2: streetLine2.trim() || undefined,
        city: city.trim(),
        state,
        zipCode: zipCode.trim(),
        country: 'US',
        isDefault,
      };

      const result = await verifyAddress(verifyRequest);

      if (result.matched) {
        // Address matches — save directly
        await saveAddress(verifyRequest);
      } else {
        // Show confirmation view
        setSuggestion(result);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to verify address');
    } finally {
      setLoading(false);
    }
  };

  const saveAddress = async (fields: {
    streetLine1: string;
    streetLine2?: string;
    city: string;
    state: string;
    zipCode: string;
  }) => {
    setLoading(true);
    setError(null);
    try {
      if (isEditMode && address) {
        const request: UpdateBuyerAddressRequest = {
          name: name.trim(),
          streetLine1: fields.streetLine1,
          streetLine2: fields.streetLine2 || undefined,
          city: fields.city,
          state: fields.state,
          zipCode: fields.zipCode,
        };
        await updateAddress(address.id, request);
      } else {
        const request: CreateBuyerAddressRequest = {
          name: name.trim(),
          streetLine1: fields.streetLine1,
          streetLine2: fields.streetLine2 || undefined,
          city: fields.city,
          state: fields.state,
          zipCode: fields.zipCode,
          country: 'US',
          isDefault,
        };
        await createAddress(request);
      }
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save address');
    } finally {
      setLoading(false);
    }
  };

  const handleUseOriginal = () => {
    if (!suggestion) return;
    const o = suggestion.original;
    saveAddress({
      streetLine1: o.streetLine1,
      streetLine2: o.streetLine2 || undefined,
      city: o.city,
      state: o.state,
      zipCode: o.zipCode,
    });
  };

  const handleUseSuggested = () => {
    if (!suggestion) return;
    const s = suggestion.suggested;
    saveAddress({
      streetLine1: s.streetLine1,
      streetLine2: s.streetLine2 || undefined,
      city: s.city,
      state: s.state,
      zipCode: s.zipCode,
    });
  };

  const formatAddress = (addr: AddressVerificationResponse['original']) => {
    const parts = [addr.streetLine1];
    if (addr.streetLine2) parts.push(addr.streetLine2);
    parts.push(`${addr.city}, ${addr.state} ${addr.zipCode}`);
    return parts;
  };

  const fieldsDiffer = (
    original: AddressVerificationResponse['original'],
    suggested: AddressVerificationResponse['suggested'],
    field: keyof AddressVerificationResponse['original']
  ) => {
    const origVal = (original[field] || '').toLowerCase();
    const sugVal = (suggested[field] || '').toLowerCase();
    return origVal !== sugVal;
  };

  // Confirmation view
  if (suggestion) {
    return (
      <div style={styles.overlay} onClick={onClose}>
        <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
          <div style={styles.header}>
            <h2 style={styles.title}>Confirm Address</h2>
            <button onClick={onClose} style={styles.closeButton}>
              <X size={20} />
            </button>
          </div>

          <div style={styles.form}>
            <p style={styles.confirmText}>
              We found a suggested address. Which would you like to use?
            </p>

            <div style={styles.comparisonRow}>
              <div style={styles.addressOption}>
                <div style={styles.addressOptionLabel}>You entered</div>
                <div style={styles.addressCard}>
                  {formatAddress(suggestion.original).map((line, i) => (
                    <div key={i} style={styles.addressLine}>{line}</div>
                  ))}
                </div>
                <button
                  onClick={handleUseOriginal}
                  style={styles.cancelButton}
                  disabled={loading}
                >
                  {loading ? 'Saving...' : 'Use Original'}
                </button>
              </div>

              <div style={styles.addressOption}>
                <div style={styles.addressOptionLabel}>Suggested</div>
                <div style={{ ...styles.addressCard, ...styles.suggestedCard }}>
                  <div style={fieldsDiffer(suggestion.original, suggestion.suggested, 'streetLine1') ? styles.diffLine : styles.addressLine}>
                    {suggestion.suggested.streetLine1}
                  </div>
                  {suggestion.suggested.streetLine2 && (
                    <div style={fieldsDiffer(suggestion.original, suggestion.suggested, 'streetLine2') ? styles.diffLine : styles.addressLine}>
                      {suggestion.suggested.streetLine2}
                    </div>
                  )}
                  <div style={
                    fieldsDiffer(suggestion.original, suggestion.suggested, 'city') ||
                    fieldsDiffer(suggestion.original, suggestion.suggested, 'state') ||
                    fieldsDiffer(suggestion.original, suggestion.suggested, 'zipCode')
                      ? styles.diffLine : styles.addressLine
                  }>
                    {suggestion.suggested.city}, {suggestion.suggested.state} {suggestion.suggested.zipCode}
                  </div>
                </div>
                <button
                  onClick={handleUseSuggested}
                  style={styles.submitButton}
                  disabled={loading}
                >
                  {loading ? 'Saving...' : 'Use Suggested'}
                </button>
              </div>
            </div>

            {error && <div style={styles.error}>{error}</div>}

            <button
              onClick={() => setSuggestion(null)}
              style={styles.backLink}
            >
              Back to editing
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h2 style={styles.title}>
            {isEditMode ? 'Edit Address' : 'Add New Address'}
          </h2>
          <button onClick={onClose} style={styles.closeButton}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Full Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={styles.input}
              placeholder="John Doe"
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Street Address *</label>
            <input
              type="text"
              value={streetLine1}
              onChange={(e) => setStreetLine1(e.target.value)}
              style={styles.input}
              placeholder="123 Main St"
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Apt, Suite, Unit (optional)</label>
            <input
              type="text"
              value={streetLine2}
              onChange={(e) => setStreetLine2(e.target.value)}
              style={styles.input}
              placeholder="Apt 4B"
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>City *</label>
            <input
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              style={styles.input}
              placeholder="Los Angeles"
            />
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>State *</label>
              <select
                value={state}
                onChange={(e) => setState(e.target.value)}
                style={styles.select}
              >
                <option value="">Select state</option>
                {US_STATES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>ZIP Code *</label>
              <input
                type="text"
                value={zipCode}
                onChange={(e) => setZipCode(e.target.value)}
                style={styles.input}
                placeholder="90001"
                maxLength={10}
              />
            </div>
          </div>

          {!isEditMode && (
            <div style={styles.formGroup}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={isDefault}
                  onChange={(e) => setIsDefault(e.target.checked)}
                  style={styles.checkbox}
                />
                <span>Set as default shipping address</span>
              </label>
            </div>
          )}

          {error && <div style={styles.error}>{error}</div>}

          <div style={styles.actions}>
            <button type="button" onClick={onClose} style={styles.cancelButton}>
              Cancel
            </button>
            <button
              type="submit"
              style={styles.submitButton}
              disabled={loading}
            >
              {loading ? 'Verifying...' : isEditMode ? 'Update' : 'Save Address'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: colors.white,
    borderRadius: '16px',
    width: '100%',
    maxWidth: '500px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: `1px solid ${colors.gray[300]}`,
  },
  title: {
    fontSize: '18px',
    fontWeight: 600,
    margin: 0,
    color: colors.ink.black,
  },
  closeButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: colors.gray[600],
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  form: {
    padding: '24px',
  },
  formGroup: {
    marginBottom: '16px',
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: 500,
    color: colors.gray[900],
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    border: `1px solid ${colors.gray[300]}`,
    borderRadius: '8px',
    fontSize: '14px',
    color: colors.ink.black,
    backgroundColor: colors.white,
    boxSizing: 'border-box',
  },
  select: {
    width: '100%',
    padding: '10px 12px',
    border: `1px solid ${colors.gray[300]}`,
    borderRadius: '8px',
    fontSize: '14px',
    color: colors.ink.black,
    backgroundColor: colors.white,
    boxSizing: 'border-box',
    appearance: 'auto',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    color: colors.ink.black,
    cursor: 'pointer',
  },
  checkbox: {
    width: '18px',
    height: '18px',
    cursor: 'pointer',
  },
  error: {
    backgroundColor: '#FEE2E2',
    border: '1px solid #FCA5A5',
    borderRadius: '8px',
    padding: '12px',
    color: '#DC2626',
    fontSize: '14px',
    marginBottom: '16px',
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    marginTop: '24px',
  },
  cancelButton: {
    padding: '10px 20px',
    backgroundColor: colors.gray[100],
    color: colors.gray[900],
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
  },
  submitButton: {
    padding: '10px 20px',
    backgroundColor: colors.brand.blue,
    color: colors.white,
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  confirmText: {
    fontSize: '14px',
    color: colors.gray[600],
    marginTop: 0,
    marginBottom: '20px',
  },
  comparisonRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
    marginBottom: '20px',
  },
  addressOption: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  addressOptionLabel: {
    fontSize: '13px',
    fontWeight: 600,
    color: colors.gray[600],
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  addressCard: {
    padding: '12px',
    borderRadius: '8px',
    border: `1px solid ${colors.gray[300]}`,
    backgroundColor: colors.gray[100],
    flex: 1,
  },
  suggestedCard: {
    borderColor: colors.brand.blue,
    backgroundColor: colors.brand.blue08,
  },
  addressLine: {
    fontSize: '14px',
    color: colors.ink.black,
    lineHeight: '1.5',
  },
  diffLine: {
    fontSize: '14px',
    color: colors.ink.black,
    lineHeight: '1.5',
    fontWeight: 700,
  },
  backLink: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    color: colors.brand.blue,
    padding: 0,
    fontWeight: 500,
  },
};
